import React, {useContext, useEffect, useRef, useState} from 'react';
import {LayoutContext} from "../../../../../../layout/context/LayoutContext";
import {Toast} from "primereact/toast";
import {usePagination} from "../../../../../../layout/hooks/usePagination";
import {useFetch} from "../../../../../../layout/hooks/useFetch";
import {webInfo} from "../../../../../../utils/webInfo";
import callToast, {
    handleChangeData,
    mergeWithSelected,
    postData,
    validateFormFields
} from "../../../../../../utils/helper";
import {Button} from "primereact/button";
import PageHeading from "../../../../../../Components/PageHeading";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import {VehicleOwnershipInterface} from "../../../../../../types/demo";
import {Tooltip} from 'primereact/tooltip';
import {Dialog} from "primereact/dialog";
import Label from "../../../../../../Components/Label";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";


const VehicleOwnership = () => {
    const initialState: VehicleOwnershipInterface = {
        vehicle_id: null,
        user_id: null,
        is_company_owned: null,
        registration_number: '',
        legal_document_link: ''
    };

    const {accessToken} = useContext(LayoutContext)
    const [refresh, setRefresh] = useState<boolean>(false);
    const [formData, setFormData] = useState<VehicleOwnershipInterface>(initialState);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const toast = useRef<Toast | null>(null)
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);

    const [modal, setModal] = useState<boolean>(false);
    const [updatedId, setUpdatedId] = useState<number | null>(null);

    const [vehicleNameFilter, setVehicleNameFilter] = useState<string>('');
    const [vehicleOptions, setVehicleOptions] = useState<{ id: number, name: string }[]>([]);

    const [userFilter, setUserFilter] = useState<string>('');
    const [userOptions, setUserOptions] = useState<{ id: number, name: string }[]>([]);

    const statuses = [
        {id: 1, name: 'Company Owned'},
        {id: 0, name: 'Personal Owned'},
    ];


    // pagination
    const {page, limitData, handlePageChange} = usePagination();
    // endpoint
    const mainTitle = "Vehicle Ownership"
    const tableDataEndpointUrl = `/vehicle-ownership?page=${page} `
    const submitFormEndpointUrl = updatedId ? `/vehicle-ownership/${updatedId}` : "/vehicle-ownership";

    // fetch data
    const {
        data: tableDataList,
        totalData, loading
    } = useFetch(tableDataEndpointUrl, [refresh])

    // fetch dorpdown data
    // parking campus
    const {data: vehicleRawData} = useFetch(
        `/vehicle?page=1&per_page=10${vehicleNameFilter ? `&model=${vehicleNameFilter}` : ''}`,
        [vehicleNameFilter]
    );

    useEffect(() => {
        if (vehicleRawData) {
            const modifiedData = vehicleRawData?.map((item: any) => ({
                id: item?.id,
                model: item?.model,
                serial_number: item?.serial_number,
            }));
            setVehicleOptions(mergeWithSelected(modifiedData, formData?.vehicle_id));
        }
    }, [vehicleRawData, formData?.vehicle_id]);


    const {data: userRawData} = useFetch(
        `/employees?page=1&per_page=10${userFilter ? `&name=${userFilter}` : ''}`,
        [userFilter]
    );

    useEffect(() => {
        if (userRawData) {
            const modifiedData = userRawData?.map((item: any) => ({
                id: item?.id,
                name: item?.name,
                email: item?.email,
            }));
            setUserOptions(mergeWithSelected(modifiedData, formData?.user_id));
        }
    }, [userRawData, formData?.user_id]);


    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        const validationRules = [
            {field: 'vehicle_id', label: 'Vehicle'},
            {field: 'user_id', label: 'User'},
            {field: 'is_company_owned', label: 'Owner type'},
            {field: 'registration_number', label: 'Registration number'},
            {field: 'legal_document_link', label: 'Legal document link'},

        ];

        if (!validateFormFields(formData, validationRules, toast)) return;

        const payload = {
            vehicle_id: formData.vehicle_id?.id,
            user_id: formData.user_id?.id,
            is_company_owned: formData.is_company_owned?.id,
            registration_number: formData.registration_number?.replace(/\s+/g, ' ').trim(),
            legal_document_link: formData.legal_document_link?.replace(/\s+/g, ' ').trim()
        };

        try {
            setIsSubmitting(true);
            const response = await fetch(`${webInfo?.baseUrl}${submitFormEndpointUrl}`, postData("PUT", accessToken, payload));


            if (response.ok) {
                callToast(toast, true, `Vehicle ${updatedId ? 'updated' : 'created'} successfully`);
                setFormData(initialState)
                setModal(false)
                setUpdatedId(null)
                setRefresh(!refresh);
                return;
            } else {
                const resData = await response.json();
                callToast(toast, false, resData.error);
            }

        } catch (err) {
            console.error('Failed to update', err);
        } finally {
            setIsSubmitting(false);
        }

    }

    // row edit template
    const actionTemplate = (rowData: any) => {

        return (
            <div className={'flex align-items-center justify-content-end'}>
                <Button icon={'pi pi-pencil'} className={'border-rounded w-2rem h-2rem'} onClick={() => {
                    const updatedStatus = rowData.is_company_owned === 1 ? {id: 1, name: 'Company Owned'} : {
                        id: 0,
                        name: 'Personal Owned'
                    };
                    setModal(true)
                    setUpdatedId(rowData?.id)
                    setFormData({
                        legal_document_link: rowData?.legal_document_link,
                        registration_number: rowData?.registration_number,
                        vehicle_id: rowData?.vehicle ?{
                            id: rowData?.vehicle?.id,
                            model: rowData?.vehicle?.model,
                            serial_number: rowData?.vehicle?.serial_number,
                        }: null,
                        user_id: rowData?.user,
                        is_company_owned: updatedStatus
                    })
                    // setFormData({
                    //     sl_number: rowData.sl_number,
                    //     model: rowData.model,
                    //     protocol: rowData.protocol,
                    //     vehicle_device_vendor_id: rowData.vehicle_device_vendor
                    // })
                }} outlined/>
            </div>
        )
    }

    const modelBodyTemplate = (rowData: any) => {
        return (
            <div className="flex flex-column gap-1">
            <span className="font-semibold text-900 text-sm">
                {rowData?.vehicle?.model || '-'}
            </span>
                <span className="text-700 text-xs">
                {rowData?.vehicle?.vehicle_type?.name || '-'}
            </span>
                <span className="text-700 text-xs">
                {rowData?.vehicle?.vehicle_brand?.name || '-'}
            </span>
            </div>
        );
    };

    const userBodyTemplate = (rowData: any) => {
        return (
            <div className="flex flex-column gap-1">
            <span className="font-semibold text-900 text-sm">
                {rowData?.user?.name || '-'}
            </span>
                <span className="text-700 text-xs">
                {rowData?.user?.email || '-'}
            </span>
            </div>
        )
    }

    function message(msg: string, status: string) {
        return <span className={`custom-badge ${status}`}>{msg}</span>;
    }

    const ownerTypeBodyTemplate = (rowData: any) => {
        return <span
            className={'w-full'}>{rowData.is_company_owned === 1 ? message("Company Owned", "secondary") : message("Personal Owned", "new")}</span>;
    }

    const legalDocumentBodyTemplate = (rowData: any) => {
        const link = rowData?.legal_document_link;
        const tooltipId = `legal-doc-tooltip-${rowData?.id}`;
        return (
            <div className="flex align-items-center justify-content-center" style={{minHeight: '2.5rem'}}>
                {link ? (
                    <>
                        <a
                            href={link}
                            target="_blank"
                            rel="noopener noreferrer"
                            id={tooltipId}
                            className="text-900 "
                            // style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <i className="pi pi-file" style={{fontSize: '1.5rem', color: '#6366f1'}}/>
                        </a>
                        <Tooltip target={`#${tooltipId}`} content={link} position="right"/>
                    </>
                ) : (
                    <span className="font-semibold text-900 text-sm">-</span>
                )}
            </div>
        );
    };

    const formVehicleDropdownItemTemplate = (option: any) => {
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">{option?.model}</span>
                <span className="text-xs text-700">{option?.serial_number}</span>
            </div>
        );
    }
    const formUserDropdownItemTemplate = (option: any) => {
        return (
            <div className="flex flex-column gap-1">
                <span className="font-semibold text-900 text-sm">{option?.name}</span>
                <span className="text-xs text-700">{option?.email}</span>
            </div>
        );
    }



    return (
        <div className={"card"}>
            <Toast ref={toast}/>
            <PageHeading title={mainTitle}
                         isCreate={true}
                         createFunction={() => setModal(true)}
                         back={true}
            />

            <DataTable
                value={tableDataList}
                dataKey="id"
                tableStyle={{minWidth: '50rem'}}
                paginator={tableDataList?.length > 0}
                lazy
                first={(page - 1) * limitData}
                rows={limitData}
                totalRecords={totalData}
                loading={loading}
                onPage={handlePageChange}
            >
                <Column
                    field="vehicle"
                    header="Serial Number"
                    style={{width: '20%'}}
                    body={(rowData: any) => rowData?.vehicle?.serial_number}
                    headerClassName={"white-space-nowrap"}/>
                <Column
                    field="vehicle"
                    header="Model"
                    style={{width: '20%'}}
                    body={modelBodyTemplate}
                    headerClassName={"white-space-nowrap"}
                />
                <Column
                    field="registration_number"
                    header="Registration Number"
                    style={{width: '20%'}}
                    headerClassName={"white-space-nowrap"}
                />

                <Column
                    field="user"
                    header="User"
                    style={{width: '20%'}}
                    body={userBodyTemplate}
                    headerClassName={"white-space-nowrap"}

                />
                <Column
                    field={"is_company_owned"}
                    header="Owner Type"
                    style={{width: '20%'}}
                    body={ownerTypeBodyTemplate}
                    headerClassName={"white-space-nowrap "}
                />

                <Column
                    field="legal_document_link"
                    header="Legal Document"
                    style={{width: '20%'}}
                    body={legalDocumentBodyTemplate}
                    headerClassName={"white-space-nowrap "}

                    bodyStyle={{textAlign: 'center'}}
                />

                <Column header={"Action"} rowEditor={true}
                        bodyStyle={{textAlign: 'center'}}
                        body={actionTemplate}
                        headerClassName={"flex justify-content-end"}
                />

            </DataTable>

            <Dialog visible={modal}
                    style={{width: "50rem"}}
                    headerClassName="text-800 font-semibold text-xl"
                    header={updatedId ? `Edit ${mainTitle}` : `New ${mainTitle}`}
                    breakpoints={{"960px": "75vw", "640px": "90vw"}}
                    onHide={() => {
                        setUpdatedId(null)
                        setModal(false)
                        setFormData(initialState)
                    }}
            >
                <div>
                    <form className={''}
                          onSubmit={handleSubmit}
                    >
                        <div className="grid ">
                            <div className="col-12  md:col-5">
                                <Label label={"Vehicle"} required={!updatedId}/>
                                <Dropdown
                                    options={vehicleOptions ? vehicleOptions : []}
                                    optionLabel="model"
                                    id="vehicle_id"
                                    name={`vehicle_id`}
                                    className={`w-full`}
                                    value={formData?.vehicle_id}
                                    onChange={(e) => handleChangeData('vehicle_id', e.value, formData, setFormData)}
                                    placeholder="Select vehicle"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setVehicleNameFilter(e.filter)
                                        }, 500);

                                        if (vehicleRawData) {
                                            const modifiedData = vehicleRawData.map((item: any) => ({
                                                id: item?.id,
                                                model: item?.model,
                                                serial_number: item?.serial_number,
                                            }));
                                            setVehicleOptions(modifiedData);
                                        }
                                    }}
                                    valueTemplate={(option)=>option? option?.model + '-' + option?.serial_number : 'Select vehicle'}
                                    itemTemplate={formVehicleDropdownItemTemplate}
                                    showClear
                                    showFilterClear
                                />
                            </div>
                            <div className="col-12  md:col-5">
                                <Label label={"Registration number"} required={!updatedId}/>
                                <InputText
                                    id="registration_number"
                                    value={formData.registration_number}
                                    size={100}
                                    name={"registration_number"}
                                    onChange={(e) => handleChangeData('registration_number', e.target.value, formData, setFormData)}
                                    placeholder={"Enter registration number"}
                                    className={`w-full`}
                                />
                            </div>
                        </div>
                        <div className={"grid mt-2"}>
                            <div className="col-12  md:col-6">
                                <Label label={"User"} required={!updatedId}/>
                                <Dropdown
                                    options={userOptions ? userOptions : []}
                                    optionLabel="name"
                                    id="user_id"
                                    name={`user_id`}
                                    className={`w-full`}
                                    value={formData?.user_id}
                                    onChange={(e) => handleChangeData('user_id', e.value, formData, setFormData)}
                                    placeholder="Select user"
                                    filter
                                    onFilter={(e) => {
                                        if (timeoutRef?.current) {
                                            clearTimeout(timeoutRef.current);
                                        }
                                        timeoutRef.current = setTimeout(() => {
                                            setUserFilter(e.filter)
                                        }, 500);

                                        if (userRawData) {
                                            const modifiedData = userRawData.map((item: any) => ({
                                                id: item?.id,
                                                name: item?.name,
                                                email: item?.email,
                                            }));
                                            setUserOptions(modifiedData);
                                        }
                                    }}
                                    // valueTemplate={(option)=>option? option?.name + '-' + option?.email : 'Select user'}
                                    itemTemplate={formUserDropdownItemTemplate}
                                    showClear
                                    showFilterClear
                                />
                            </div>

                            <div className="col-12 md:col-6">
                                <Label label={"Owner type"} required={!updatedId}/>
                                <Dropdown
                                    options={statuses}
                                    id={"is_company_owned"}
                                    name={"is_company_owned"}
                                    placeholder={"Select owner type"}
                                    optionLabel="name"
                                    value={formData?.is_company_owned}
                                    onChange={(e) => handleChangeData('is_company_owned', e.value, formData, setFormData)}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        <div className="grid mt-2">

                            <div className="col-12  md:col-8">
                                <Label label={"Legal document link"} required={!updatedId}/>
                                <InputText
                                    id="legal_document_link"
                                    value={formData.legal_document_link}
                                    name={"legal_document_link"}
                                    onChange={(e) => handleChangeData('legal_document_link', e.target.value, formData, setFormData)}
                                    placeholder={"Enter legal document link"}
                                    className={`w-full`}
                                />
                            </div>

                        </div>
                        <div className="flex gap-2 mt-2">
                            <Button
                                type="submit"
                                label={updatedId ? 'Update' : "Create"}
                                className="p-button-primary"
                                size={'small'}
                                loading={isSubmitting}
                            />
                        </div>
                    </form>
                </div>
            </Dialog>

        </div>
    );
};

export default VehicleOwnership;

